import { IconAdjustmentsHorizontal } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverClose, PopoverPopup, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import type { PolicyFilter, RunFilter } from "./campaign-list-filters";

export function CampaignFilter({
  policyFilters,
  runFilters,
  managedGroupFilters,
  managedGroupOptions,
  onPolicyChange,
  onRunChange,
  onManagedGroupChange,
}: {
  policyFilters: PolicyFilter[];
  runFilters: RunFilter[];
  managedGroupFilters: string[];
  managedGroupOptions: Array<{ value: string; label: string }>;
  onPolicyChange: (values: PolicyFilter[]) => void;
  onRunChange: (values: RunFilter[]) => void;
  onManagedGroupChange: (values: string[]) => void;
}) {
  const { t } = useTranslation("campaign");
  const toggle = <T extends string>(values: T[], value: T) =>
    values.includes(value) ? values.filter((item) => item !== value) : [...values, value];
  const activeCount = policyFilters.length + runFilters.length + managedGroupFilters.length;
  return (
    <Popover>
      <PopoverTrigger
        render={
          <Button
            variant="outline"
            size="xs"
            className={cn(
              "h-8 min-w-[77px] gap-[7px] rounded-[8px] px-2.5 text-[11px] leading-[14px]",
              activeCount > 0 && "border-transparent bg-control-highlight",
            )}
          />
        }
      >
        <IconAdjustmentsHorizontal data-icon="inline-start" />
        {t("list.filters.button")}
        {activeCount > 0 ? ` · ${activeCount}` : ""}
      </PopoverTrigger>
      <PopoverPopup
        align="end"
        sideOffset={8}
        initialFocus={false}
        className="w-[320px] overflow-hidden rounded-[10px] p-0 [&>[data-slot=popover-viewport]]:p-0"
      >
        <div className="flex flex-col">
          <FilterGroup
            title={t("list.filters.policyTitle")}
            options={[
              { value: "managed_group", label: t("list.policyLabel.managedGroup") },
              { value: "inherited", label: t("list.policyLabel.inherited") },
              { value: "override", label: t("list.policyLabel.override") },
              { value: "disabled", label: t("list.policyLabel.disabled") },
              { value: "excluded", label: t("list.policyLabel.excluded") },
            ]}
            selected={policyFilters}
            onToggle={(value) => onPolicyChange(toggle(policyFilters, value as PolicyFilter))}
            divided
          />
          <FilterGroup
            title={t("list.filters.managedGroupTitle")}
            options={managedGroupOptions}
            selected={managedGroupFilters}
            onToggle={(value) => onManagedGroupChange(toggle(managedGroupFilters, value))}
            divided
          />
          <FilterGroup
            title={t("list.filters.runTitle")}
            options={[
              { value: "attention", label: t("list.filters.run.attention") },
              { value: "analyzing", label: t("list.filters.run.analyzing") },
              { value: "completed", label: t("list.filters.run.completed") },
              { value: "never", label: t("list.filters.run.never") },
            ]}
            selected={runFilters}
            onToggle={(value) => onRunChange(toggle(runFilters, value as RunFilter))}
          />
          <div className="flex h-[52px] items-center justify-between border-t border-line bg-subtle pl-3.5 pr-3">
            <Button
              variant="ghost"
              size="xs"
              className="h-[30px] rounded-[8px] text-xs"
              onClick={() => {
                onPolicyChange([]);
                onRunChange([]);
                onManagedGroupChange([]);
              }}
            >
              {t("list.filters.clearAll")}
            </Button>
            <PopoverClose
              render={
                <Button size="sm" className="h-[30px] rounded-[8px] px-3 text-xs leading-4" />
              }
            >
              {t("list.filters.apply")}
            </PopoverClose>
          </div>
        </div>
      </PopoverPopup>
    </Popover>
  );
}

function FilterGroup({
  title,
  options,
  selected,
  onToggle,
  divided = false,
}: {
  title: string;
  options: Array<{ value: string; label: string }>;
  selected: string[];
  onToggle: (value: string) => void;
  divided?: boolean;
}) {
  return (
    <div className={cn("flex flex-col gap-2.5 px-3.5 py-3", divided && "border-b border-line")}>
      <p className="text-[11px] font-semibold leading-[14px] text-ink">{title}</p>
      <div className="flex flex-wrap gap-x-3 gap-y-2">
        {options.map((option) => (
          <button
            type="button"
            key={option.value}
            className="flex h-5 w-[136px] shrink-0 cursor-pointer items-center gap-2 text-left text-xs leading-4 text-ink"
            onClick={() => onToggle(option.value)}
          >
            <Checkbox
              checked={selected.includes(option.value)}
              tabIndex={-1}
              aria-hidden="true"
              className="size-3.5 rounded-[4px] [&_[data-slot=checkbox-indicator]>svg]:size-2.5"
            />
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}
